import { useState, useEffect } from 'react';
import { Container, Header, Content, Navbar, Table, Button, Panel, Message, Tag, TagGroup, IconButton, Tooltip, Whisper, InputGroup, Input, Modal, ButtonToolbar, useToaster, Form, SelectPicker } from 'rsuite';
import { Search, Edit as EditIcon, Plus, Icon, Trash } from '@rsuite/icons';
import { FaPlay, FaStop } from 'react-icons/fa';
import { Page } from '@rsuite/icons';
import { getApiUrl, getAuthHeaders, getUserId } from './utils/apiConfig';
import { useAuth } from './utils/useAuth';
import { formatDateTime, formatUptime } from './utils/dateUtils';
import InferenceLogDialog from './components/InferenceLogDialog';
import InferenceDetailsDialog from './components/InferenceDetailsDialog';
import CopyButton from './components/CopyButton';

const { Column, HeaderCell, Cell } = Table;

const getStatusColor = (status) => {
  switch (status) {
    case 'ready':
      return 'green';
    case 'running':
      return 'green';
    case 'creating':
      return 'yellow';
    case 'stopped':
      return 'default';
    case 'error':
      return 'red';
    default:
      return 'default';
  }
};

const fetchModels = async (getAccessTokenSilently, user) => {
  // Don't try to fetch if getAccessTokenSilently is not available
  if (!getAccessTokenSilently) {
    throw new Error('Authentication required. Auth0 token not available.');
  }
  
  const userId = getUserId(user);
  if (!userId) {
    throw new Error('User ID not found. Please set up your user ID first.');
  }
  
  const headers = await getAuthHeaders(getAccessTokenSilently);
  const response = await fetch(getApiUrl(`/users/${userId}/inference/models`), {
    method: 'GET',
    headers: headers,
  });
  
  if (response.status === 401) {
    throw new Error('Unauthorized - Authentication required');
  }
  
  if (!response.ok) {
    throw new Error(`Failed to fetch models: ${response.statusText}`);
  }
  
  const data = await response.json();
  return data.models || [];
};

const fetchModelStatus = async (modelId, getAccessTokenSilently, user) => {
  if (!getAccessTokenSilently) {
    throw new Error('Authentication required. Auth0 token not available.');
  }
  
  const userId = getUserId(user);
  if (!userId) {
    throw new Error('User ID not found. Please set up your user ID first.');
  }
  
  const headers = await getAuthHeaders(getAccessTokenSilently);
  const response = await fetch(getApiUrl(`/users/${userId}/inference/models/${modelId}`), {
    method: 'GET',
    headers: headers,
  });
  
  if (response.status === 401) {
    throw new Error('Unauthorized - Authentication required');
  }
  
  if (!response.ok) {
    throw new Error(`Failed to fetch status: ${response.statusText}`);
  }
  
  return await response.json();
};

const initModel = async (modelId, getAccessTokenSilently, user) => {
  if (!getAccessTokenSilently) {
    throw new Error('Authentication required. Auth0 token not available.');
  }
  
  const userId = getUserId(user);
  if (!userId) {
    throw new Error('User ID not found. Please set up your user ID first.');
  }
  
  const headers = await getAuthHeaders(getAccessTokenSilently);
  const response = await fetch(getApiUrl(`/users/${userId}/inference/models/${modelId}/run`), {
    method: 'POST',
    headers: headers,
  });
  
  if (response.status === 401) {
    throw new Error('Unauthorized - Authentication required');
  }
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.message || `Failed to initialize model: ${response.statusText}`);
  }
  
  return await response.json();
};

const killModel = async (modelId, getAccessTokenSilently, user) => {
  if (!getAccessTokenSilently) {
    throw new Error('Authentication required. Auth0 token not available.');
  }
  
  const userId = getUserId(user);
  if (!userId) {
    throw new Error('User ID not found. Please set up your user ID first.');
  }
  
  const headers = await getAuthHeaders(getAccessTokenSilently);
  const response = await fetch(getApiUrl(`/users/${userId}/inference/models/${modelId}`), {
    method: 'DELETE',
    headers: headers,
  });
  
  if (response.status === 401) {
    throw new Error('Unauthorized - Authentication required');
  }
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.message || `Failed to kill model: ${response.statusText}`);
  }
  
  return await response.json();
};

const unregisterModel = async (modelId, getAccessTokenSilently, user) => {
  if (!getAccessTokenSilently) {
    throw new Error('Authentication required. Auth0 token not available.');
  }
  
  const userId = getUserId(user);
  if (!userId) {
    throw new Error('User ID not found. Please set up your user ID first.');
  }
  
  const headers = await getAuthHeaders(getAccessTokenSilently);
  const response = await fetch(getApiUrl(`/users/${userId}/inference/unregister/${modelId}`), {
    method: 'DELETE',
    headers: headers,
  });
  
  if (response.status === 401) {
    throw new Error('Unauthorized - Authentication required');
  }
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.message || `Failed to unregister model: ${response.statusText}`);
  }
  
  return await response.json();
};

const updateModel = async (modelId, updateData, getAccessTokenSilently, user) => {
  if (!getAccessTokenSilently) {
    throw new Error('Authentication required. Auth0 token not available.');
  }
  
  const userId = getUserId(user);
  if (!userId) {
    throw new Error('User ID not found. Please set up your user ID first.');
  }
  
  const headers = await getAuthHeaders(getAccessTokenSilently);
  const response = await fetch(getApiUrl(`/users/${userId}/inference/models/${modelId}`), {
    method: 'PUT',
    headers: {
      ...headers,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updateData),
  });
  
  if (response.status === 401) {
    throw new Error('Unauthorized - Authentication required');
  }
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.message || `Failed to update model: ${response.statusText}`);
  }
  
  return await response.json();
};

const registerModel = async (modelData, getAccessTokenSilently, user) => {
  if (!getAccessTokenSilently) {
    throw new Error('Authentication required. Auth0 token not available.');
  }
  
  const userId = getUserId(user);
  if (!userId) {
    throw new Error('User ID not found. Please set up your user ID first.');
  }
  
  // Extract model_id from modelData and remove it from the payload (it's now in the URL)
  const modelId = modelData.model_id;
  if (!modelId) {
    throw new Error('model_id is required');
  }
  
  // Create payload without model_id (it goes in the URL)
  const { model_id, ...payload } = modelData;
  
  const headers = await getAuthHeaders(getAccessTokenSilently);
  const response = await fetch(getApiUrl(`/users/${userId}/inference/models/${modelId}`), {
    method: 'POST',
    headers: headers,
    body: JSON.stringify(payload),
  });
  
  if (response.status === 401) {
    throw new Error('Unauthorized - Authentication required');
  }
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.detail || errorData.message || `Failed to register model: ${response.statusText}`;
    // Handle both string and object detail formats
    if (typeof errorMessage === 'object' && errorMessage.message) {
      throw new Error(errorMessage.message);
    }
    throw new Error(errorMessage);
  }
  
  return await response.json();
};

const fetchDroplets = async (getAccessTokenSilently) => {
  if (!getAccessTokenSilently) {
    throw new Error('Authentication required. Auth0 token not available.');
  }
  
  const headers = await getAuthHeaders(getAccessTokenSilently);
  const response = await fetch(getApiUrl('/instances'), {
    method: 'GET',
    headers: headers,
  });
  
  if (response.status === 401) {
    throw new Error('Unauthorized - Authentication required');
  }
  
  if (!response.ok) {
    throw new Error(`Failed to fetch instances: ${response.statusText}`);
  }
  
  const data = await response.json();
  return data.sizes || [];
};

const fetchRegions = async (getAccessTokenSilently) => {
  if (!getAccessTokenSilently) {
    throw new Error('Authentication required. Auth0 token not available.');
  }
  
  const headers = await getAuthHeaders(getAccessTokenSilently);
  const response = await fetch(getApiUrl('/regions'), {
    method: 'GET',
    headers: headers,
  });
  
  if (response.status === 401) {
    throw new Error('Unauthorized - Authentication required');
  }
  
  if (!response.ok) {
    throw new Error(`Failed to fetch regions: ${response.statusText}`);
  }
  
  const data = await response.json();
  return data.regions || [];
};

const fetchSavedModels = async (getAccessTokenSilently, user) => {
  if (!getAccessTokenSilently) {
    throw new Error('Authentication required. Auth0 token not available.');
  }
  
  const userId = getUserId(user);
  if (!userId) {
    throw new Error('User ID not found. Please set up your user ID first.');
  }
  
  const headers = await getAuthHeaders(getAccessTokenSilently);
  const response = await fetch(getApiUrl(`/users/${userId}/models`), {
    method: 'GET',
    headers: headers,
  });
  
  if (response.status === 401) {
    throw new Error('Unauthorized - Authentication required');
  }
  
  if (!response.ok) {
    throw new Error(`Failed to fetch saved models: ${response.statusText}`);
  }
  
  const data = await response.json();
  return data.models || [];
};

function Inference() {
  const auth = useAuth();
  const toaster = useToaster();
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [modelDetails, setModelDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [droplets, setDroplets] = useState([]);
  const [savedModels, setSavedModels] = useState([]);
  const [formValue, setFormValue] = useState({
    model_id: '',
    instance_type: '',
    origin_model_id: '',
    description: '',
    adapter: '',
    idle_timeout: 1800,
    provider_options: ''
  });
  const [registering, setRegistering] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [editFormValue, setEditFormValue] = useState({
    base_model_id: '',
    instance_type: '',
    description: '',
    adapter: '',
    idle_timeout: 1800,
    provider_options: ''
  });
  const [savingDetails, setSavingDetails] = useState(false);

  // Trigger refresh when auth becomes ready
  useEffect(() => {
    if (auth.hasAuth0 && !auth.isLoading && auth.isAuthenticated && auth.getAccessTokenSilently) {
      setRefreshKey(prev => prev + 1);
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.hasAuth0]);

  const loadModels = async () => {
    // Don't try to load if Auth0 is still loading or user is not authenticated
    if (auth.hasAuth0 && (auth.isLoading || !auth.isAuthenticated || !auth.getAccessTokenSilently)) {
      return;
    }
    
    setLoading(true);
    try {
      const data = await fetchModels(auth.getAccessTokenSilently, auth.user);
      setModels(data);
    } catch (err) {
      console.error('Error loading models:', err);
      toaster.push(
        <Message type="error" showIcon>
          {err.message}
        </Message>,
        { placement: 'bottomCenter', duration: 5000 }
      );
    } finally {
      setLoading(false);
    }
  };

  const loadModelDetails = async (modelId) => {
    if (auth.hasAuth0 && (auth.isLoading || !auth.isAuthenticated || !auth.getAccessTokenSilently)) {
      return;
    }
    
    setDetailsLoading(true);
    try {
      const details = await fetchModelStatus(modelId, auth.getAccessTokenSilently, auth.user);
      setModelDetails(details);
    } catch (err) {
      console.error('Error loading model details:', err);
      setModelDetails({ error: err.message });
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleViewDetails = async (model) => {
    if (auth.hasAuth0 && (auth.isLoading || !auth.isAuthenticated || !auth.getAccessTokenSilently)) {
      toaster.push(
        <Message type="error" showIcon>
          Authentication required. Please wait for Auth0 to initialize.
        </Message>,
        { placement: 'bottomCenter', duration: 5000 }
      );
      return;
    }
    
    setSelectedModel(model);
    setShowDetailsModal(true);
    setModelDetails(null);
    setDetailsLoading(true);
    setEditingDetails(false);
    
    try {
      const details = await fetchModelStatus(model.id, auth.getAccessTokenSilently, auth.user);
      setModelDetails(details);
      // Initialize edit form with current values
      setEditFormValue({
        base_model_id: details.origin_model_id || details.base_model_id || '',
        instance_type: details.instance_type || '',
        description: details.description || '',
        adapter: details.adapter || '',
        idle_timeout: details.idle_timeout || 1800,
        provider_options: details.provider_options && Object.keys(details.provider_options).length > 0 ? JSON.stringify(details.provider_options, null, 2) : ''
      });
    } catch (err) {
      console.error('Error fetching model details:', err);
      setModelDetails({ error: err.message });
      toaster.push(
        <Message type="error" showIcon>
          {err.message}
        </Message>,
        { placement: 'bottomCenter', duration: 5000 }
      );
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleSaveDetails = async () => {
    if (auth.hasAuth0 && (auth.isLoading || !auth.isAuthenticated || !auth.getAccessTokenSilently)) {
      toaster.push(
        <Message type="error" showIcon>
          Authentication required. Please wait for Auth0 to initialize.
        </Message>,
        { placement: 'bottomCenter', duration: 5000 }
      );
      return;
    }

    if (!selectedModel) {
      return;
    }

    setSavingDetails(true);
    try {
      const updateData = {};
      
      // Only include fields that are being updated
      const currentBaseModelId = modelDetails?.origin_model_id || modelDetails?.base_model_id || '';
      if (editFormValue.base_model_id !== currentBaseModelId) {
        updateData.base_model_id = editFormValue.base_model_id || '';
      }
      
      const currentInstanceType = modelDetails?.instance_type || '';
      if (editFormValue.instance_type !== currentInstanceType) {
        updateData.instance_type = editFormValue.instance_type;
      }

      const currentDescription = modelDetails?.description || '';
      if (editFormValue.description !== currentDescription) {
        updateData.description = editFormValue.description || '';
      }
      
      const currentAdapter = modelDetails?.adapter || '';
      if (editFormValue.adapter !== currentAdapter) {
        updateData.adapter = editFormValue.adapter || null;
      }
      
      if (editFormValue.idle_timeout !== (modelDetails?.idle_timeout || 1800)) {
        updateData.idle_timeout = parseInt(editFormValue.idle_timeout, 10);
      }

      const currentProviderOptionsStr = modelDetails?.provider_options && Object.keys(modelDetails.provider_options).length > 0 ? JSON.stringify(modelDetails.provider_options, null, 2) : '';
      if (editFormValue.provider_options !== currentProviderOptionsStr) {
        if (editFormValue.provider_options && editFormValue.provider_options.trim()) {
          try {
            updateData.provider_options = JSON.parse(editFormValue.provider_options);
          } catch (e) {
            toaster.push(
              <Message type="error" showIcon>
                Provider Options must be valid JSON.
              </Message>,
              { placement: 'bottomCenter', duration: 5000 }
            );
            setSavingDetails(false);
            return;
          }
        } else {
          updateData.provider_options = {};
        }
      }

      if (Object.keys(updateData).length === 0) {
        toaster.push(
          <Message type="info" showIcon>
            No changes to save.
          </Message>,
          { placement: 'bottomCenter', duration: 3000 }
        );
        setEditingDetails(false);
        return;
      }

      const result = await updateModel(selectedModel.id, updateData, auth.getAccessTokenSilently, auth.user);
      toaster.push(
        <Message type="success" showIcon>
          Model {selectedModel.id} updated successfully!
        </Message>,
        { placement: 'bottomCenter', duration: 5000 }
      );
      
      // Reload model details
      const details = await fetchModelStatus(selectedModel.id, auth.getAccessTokenSilently, auth.user);
      setModelDetails(details);
      setEditFormValue({
        base_model_id: details.origin_model_id || details.base_model_id || '',
        instance_type: details.instance_type || '',
        description: details.description || '',
        adapter: details.adapter || '',
        idle_timeout: details.idle_timeout || 1800,
        provider_options: details.provider_options && Object.keys(details.provider_options).length > 0 ? JSON.stringify(details.provider_options, null, 2) : ''
      });
      setEditingDetails(false);
      
      // Reload models list
      await loadModels();
    } catch (err) {
      toaster.push(
        <Message type="error" showIcon>
          {err.message}
        </Message>,
        { placement: 'bottomCenter', duration: 5000 }
      );
    } finally {
      setSavingDetails(false);
    }
  };

  const handleViewLogs = (model) => {
    setSelectedModel(model);
    setShowLogsModal(true);
  };

  useEffect(() => {
    // Only load models if Auth0 is ready and user is authenticated
    if (auth.hasAuth0 && !auth.isLoading && auth.isAuthenticated && auth.getAccessTokenSilently) {
      loadModels();
      loadDropletsAndRegions();
      loadSavedModels();
    } else if (!auth.hasAuth0) {
      // If Auth0 is not configured, still try to load (might use API key)
      loadModels();
      loadDropletsAndRegions();
      loadSavedModels();
    }
  }, [refreshKey, auth.isLoading, auth.isAuthenticated, auth.hasAuth0]);

  const loadDropletsAndRegions = async () => {
    if (auth.hasAuth0 && (auth.isLoading || !auth.isAuthenticated || !auth.getAccessTokenSilently)) {
      return;
    }
    
    try {
      const dropletsData = await fetchDroplets(auth.getAccessTokenSilently);
      setDroplets(dropletsData);
    } catch (err) {
      console.error('Error loading droplets:', err);
    }
  };

  const loadSavedModels = async () => {
    if (auth.hasAuth0 && (auth.isLoading || !auth.isAuthenticated || !auth.getAccessTokenSilently)) {
      return;
    }
    
    try {
      const modelsData = await fetchSavedModels(auth.getAccessTokenSilently, auth.user);
      setSavedModels(modelsData);
    } catch (err) {
      console.error('Error loading saved models:', err);
    }
  };

  const handleRegister = async () => {
    if (auth.hasAuth0 && (auth.isLoading || !auth.isAuthenticated || !auth.getAccessTokenSilently)) {
      toaster.push(
        <Message type="error" showIcon>
          Authentication required. Please wait for Auth0 to initialize.
        </Message>,
        { placement: 'bottomCenter', duration: 5000 }
      );
      return;
    }

    // Validate required fields
    if (!formValue.model_id || !formValue.instance_type || !formValue.origin_model_id) {
      toaster.push(
        <Message type="error" showIcon>
          Please fill in all required fields (Model ID, Base Model ID, Instance Type).
        </Message>,
        { placement: 'bottomCenter', duration: 5000 }
      );
      return;
    }

    // Validate model_id doesn't contain "/"
    if (formValue.model_id.includes('/')) {
      toaster.push(
        <Message type="error" showIcon>
          Model ID cannot contain "/" character.
        </Message>,
        { placement: 'bottomCenter', duration: 5000 }
      );
      return;
    }

    if ((formValue.description || '').length > 2048) {
      toaster.push(
        <Message type="error" showIcon>
          Description exceeds maximum length of 2048 characters.
        </Message>,
        { placement: 'bottomCenter', duration: 5000 }
      );
      return;
    }

    setRegistering(true);
    try {
      // Prepare model data
      const modelData = {
        model_id: formValue.model_id,
        instance_type: formValue.instance_type,
        origin_model_id: formValue.origin_model_id,
        description: formValue.description || '',
      };

      // Add optional adapter field if provided
      if (formValue.adapter) {
        modelData.adapter = formValue.adapter;
      }

      // Add idle_timeout field (default: 1800 seconds = 30 minutes)
      if (formValue.idle_timeout) {
        modelData.idle_timeout = parseInt(formValue.idle_timeout, 10);
      }

      if (formValue.provider_options && formValue.provider_options.trim()) {
        try {
          modelData.provider_options = JSON.parse(formValue.provider_options);
        } catch (e) {
          toaster.push(
            <Message type="error" showIcon>
              Provider Options must be valid JSON.
            </Message>,
            { placement: 'bottomCenter', duration: 5000 }
          );
          setRegistering(false);
          return;
        }
      }

      const result = await registerModel(modelData, auth.getAccessTokenSilently, auth.user);
      toaster.push(
        <Message type="success" showIcon>
          Model {result.model_id} registered successfully!
        </Message>,
        { placement: 'bottomCenter', duration: 5000 }
      );
      
      // Reset form and close modal
      setFormValue({
        model_id: '',
        instance_type: '',
        origin_model_id: '',
        description: '',
        adapter: '',
        idle_timeout: 1800
      });
      setShowRegisterModal(false);
      
      // Reload models
      await loadModels();
    } catch (err) {
      toaster.push(
        <Message type="error" showIcon>
          {err.message}
        </Message>,
        { placement: 'bottomCenter', duration: 5000 }
      );
    } finally {
      setRegistering(false);
    }
  };

  const handleInit = async (modelId) => {
    if (auth.hasAuth0 && (auth.isLoading || !auth.isAuthenticated || !auth.getAccessTokenSilently)) {
      toaster.push(
        <Message type="error" showIcon>
          Authentication required. Please wait for Auth0 to initialize.
        </Message>,
        { placement: 'bottomCenter', duration: 5000 }
      );
      return;
    }
    
    try {
      const result = await initModel(modelId, auth.getAccessTokenSilently, auth.user);
      const message = result.message || 'Success';
      toaster.push(
        <Message type="success" showIcon>
          Model {modelId} initialization started: {message}
        </Message>,
        { placement: 'bottomCenter', duration: 5000 }
      );
      await loadModels();
    } catch (err) {
      toaster.push(
        <Message type="error" showIcon>
          {err.message}
        </Message>,
        { placement: 'bottomCenter', duration: 5000 }
      );
    }
  };

  const handleKill = async (modelId) => {
    if (auth.hasAuth0 && (auth.isLoading || !auth.isAuthenticated || !auth.getAccessTokenSilently)) {
      toaster.push(
        <Message type="error" showIcon>
          Authentication required. Please wait for Auth0 to initialize.
        </Message>,
        { placement: 'bottomCenter', duration: 5000 }
      );
      return;
    }
    
    try {
      const result = await killModel(modelId, auth.getAccessTokenSilently, auth.user);
      const message = result.message || 'Success';
      toaster.push(
        <Message type="success" showIcon>
          Model {modelId} droplet released: {message}
        </Message>,
        { placement: 'bottomCenter', duration: 5000 }
      );
      await loadModels();
    } catch (err) {
      toaster.push(
        <Message type="error" showIcon>
          {err.message}
        </Message>,
        { placement: 'bottomCenter', duration: 5000 }
      );
    }
  };

  const handleDelete = async (modelId) => {
    if (auth.hasAuth0 && (auth.isLoading || !auth.isAuthenticated || !auth.getAccessTokenSilently)) {
      toaster.push(
        <Message type="error" showIcon>
          Authentication required. Please wait for Auth0 to initialize.
        </Message>,
        { placement: 'bottomCenter', duration: 5000 }
      );
      return;
    }
    
    try {
      const result = await unregisterModel(modelId, auth.getAccessTokenSilently, auth.user);
      const message = result.message || 'Success';
      toaster.push(
        <Message type="success" showIcon>
          Model {modelId} removed: {message}
        </Message>,
        { placement: 'bottomCenter', duration: 5000 }
      );
      setShowDeleteModal(false);
      setSelectedModel(null);
      await loadModels();
    } catch (err) {
      toaster.push(
        <Message type="error" showIcon>
          {err.message}
        </Message>,
        { placement: 'bottomCenter', duration: 5000 }
      );
    }
  };

  const filteredModels = models.filter(model => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      model.id.toLowerCase().includes(searchLower) ||
      ((model.origin_model_id || model.base_model_id) && (model.origin_model_id || model.base_model_id).toLowerCase().includes(searchLower)) ||
      (model.description && model.description.toLowerCase().includes(searchLower)) ||
      (model.instance_type && model.instance_type.toLowerCase().includes(searchLower))
    );
  });

  return (
    <Container>
      <Header style={{ position: 'sticky', top: 0, zIndex: 1000 }}>
        <Navbar style={{ display: 'flex', alignItems: 'center' }}>
          <Navbar.Brand>
            Inference Models
          </Navbar.Brand>
          <div style={{ marginLeft: 'auto', marginRight: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <InputGroup inside style={{ width: 300 }}>
              <Input
                placeholder="Search by Model ID, Base Model, Description, or Instance Type"
                value={searchTerm}
                onChange={(value) => setSearchTerm(value)}
              />
              <InputGroup.Addon>
                <Search />
              </InputGroup.Addon>
            </InputGroup>
            <Button appearance="primary" onClick={loadModels} loading={loading}>
              Refresh
            </Button>
            <Button appearance="primary" color="green" icon={<Plus />} onClick={() => setShowRegisterModal(true)}>
              Register Model
            </Button>
          </div>
        </Navbar>
      </Header>
      <Content>
        <Panel>
            <Table
              data={filteredModels}
              autoHeight
              rowHeight={60}
              loading={loading}
              hover
              renderEmpty={() => (
                <div style={{ padding: '40px', textAlign: 'center' }}>
                  <div style={{ marginBottom: '20px', color: '#999', fontSize: '1em' }}>
                    No data found
                  </div>
                  <Button 
                    appearance="primary" 
                    color="green" 
                    icon={<Plus />} 
                    onClick={() => setShowRegisterModal(true)}
                  >
                    Add model
                  </Button>
                </div>
              )}
            >
                <Column width={120} fixed>
                  <HeaderCell>Status</HeaderCell>
                  <Cell>
                    {(rowData) => (
                      <Tag color={getStatusColor(rowData.status)}>
                        {rowData.status}
                      </Tag>
                    )}
                  </Cell>
                </Column>
                <Column flexGrow={1}>
                  <HeaderCell>Model ID</HeaderCell>
                  <Cell>
                    {(rowData) => (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{rowData.id}</span>
                        <div onClick={(e) => e.stopPropagation()}>
                          <CopyButton
                            text={rowData.id}
                            size="sm"
                            successMessage="Model ID copied to clipboard"
                            errorMessage="Failed to copy model ID to clipboard"
                          />
                        </div>
                      </div>
                    )}
                  </Cell>
                </Column>
                <Column width={150}>
                  <HeaderCell>Instance Type</HeaderCell>
                  <Cell>
                    {(rowData) => {
                      if (!rowData.instance_type) {
                        return '-';
                      }
                      return (
                        <Whisper
                          placement="topStart"
                          trigger="hover"
                          speaker={<Tooltip>{rowData.instance_type}</Tooltip>}
                        >
                          <span style={{ display: 'inline-block', cursor: 'default' }}>
                            {rowData.instance_type}
                          </span>
                        </Whisper>
                      );
                    }}
                  </Cell>
                </Column>
                <Column flexGrow={1} minWidth={180}>
                  <HeaderCell>Description</HeaderCell>
                  <Cell>
                    {(rowData) => {
                      const description = rowData.description;
                      if (!description) {
                        return '-';
                      }
                      const truncated = (
                        <div
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {description}
                        </div>
                      );
                      return (
                        <Whisper
                          placement="topStart"
                          trigger="hover"
                          speaker={<Tooltip style={{ maxWidth: 480 }}>{description}</Tooltip>}
                        >
                          {truncated}
                        </Whisper>
                      );
                    }}
                  </Cell>
                </Column>
                <Column width={100}>
                  <HeaderCell>Uptime</HeaderCell>
                  <Cell>
                    {(rowData) => formatUptime(rowData.uptime_seconds)}
                  </Cell>
                </Column>
                <Column width={200}>
                  <HeaderCell>Base Model</HeaderCell>
                  <Cell>
                    {(rowData) => rowData.origin_model_id || rowData.id}
                  </Cell>
                </Column>
                <Column width={200}>
                  <HeaderCell>Adapter</HeaderCell>
                  <Cell>
                    {(rowData) => (
                      rowData.adapter || '-'
                    )}
                  </Cell>
                </Column>
                <Column width={200} fixed="right">
                  <HeaderCell>Actions</HeaderCell>
                  <Cell>
                    {(rowData) => (
                      <ButtonToolbar style={{ whiteSpace: 'nowrap' }}>
                        <Whisper
                          placement="top"
                          trigger="hover"
                          speaker={<Tooltip>View details</Tooltip>}
                        >
                          <IconButton
                            icon={<EditIcon />}
                            color="blue"
                            appearance="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewDetails(rowData);
                            }}
                          />
                        </Whisper>
                        <Whisper
                          placement="top"
                          trigger="hover"
                          speaker={<Tooltip>View logs</Tooltip>}
                        >
                          <IconButton
                            icon={<Page />}
                            color="yellow"
                            appearance="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewLogs(rowData);
                            }}
                          />
                        </Whisper>
                        {rowData.status === 'stopped' || rowData.status === 'error' ? (
                          <>
                            <Whisper placement="top" trigger="hover" speaker={<Tooltip>Run Model</Tooltip>}>
                              <IconButton
                                icon={<Icon as={FaPlay} />}
                                color="green"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleInit(rowData.id);
                                }}
                              />
                            </Whisper>
                            <Whisper placement="top" trigger="hover" speaker={<Tooltip>Remove Model</Tooltip>}>
                              <IconButton
                                icon={<Trash />}
                                color="red"
                                appearance="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedModel(rowData);
                                  setShowDeleteModal(true);
                                }}
                              />
                            </Whisper>
                          </>
                        ) : (
                          <Whisper placement="top" trigger="hover" speaker={<Tooltip>Release Model</Tooltip>}>
                            <IconButton
                              icon={<Icon as={FaStop} />}
                              color="red"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleKill(rowData.id);
                              }}
                            />
                          </Whisper>
                        )}
                      </ButtonToolbar>
                    )}
                  </Cell>
                </Column>
              </Table>

            {filteredModels.length > 0 && (
              <div style={{ marginTop: 10, color: '#999', fontSize: '0.9em' }}>
                Showing {filteredModels.length} of {models.length} model{models.length !== 1 ? 's' : ''}
              </div>
            )}
          </Panel>

          {/* Details Modal */}
          <InferenceDetailsDialog
            show={showDetailsModal}
            onClose={() => {
              setShowDetailsModal(false);
              setEditingDetails(false);
            }}
            modelId={selectedModel?.id}
            modelDetails={modelDetails}
            detailsLoading={detailsLoading}
            editingDetails={editingDetails}
            editFormValue={editFormValue}
            savingDetails={savingDetails}
            droplets={droplets}
            savedModels={savedModels}
            onEditChange={setEditFormValue}
            onSave={handleSaveDetails}
            onCancelEdit={() => {
              setEditingDetails(false);
              // Reset edit form to current values
              setEditFormValue({
                base_model_id: modelDetails?.origin_model_id || modelDetails?.base_model_id || '',
                instance_type: modelDetails?.instance_type || '',
                description: modelDetails?.description || '',
                adapter: modelDetails?.adapter || '',
                idle_timeout: modelDetails?.idle_timeout || 1800,
                provider_options: modelDetails?.provider_options && Object.keys(modelDetails.provider_options).length > 0 ? JSON.stringify(modelDetails.provider_options, null, 2) : ''
              });
            }}
            onStartEdit={() => {
              setEditingDetails(true);
            }}
          />

          {/* Register Model Modal */}
          <Modal 
            open={showRegisterModal} 
            onClose={() => {
              setShowRegisterModal(false);
              setFormValue({
                model_id: '',
                instance_type: '',
                origin_model_id: '',
                description: '',
                adapter: '',
                idle_timeout: 1800,
                provider_options: ''
              });
            }}
            size="lg"
          >
            <Modal.Header>
              <Modal.Title>Register New Inference Model</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form formValue={formValue} onChange={setFormValue} fluid>
                <Form.Group>
                  <Form.ControlLabel>Model ID <span style={{ color: 'red' }}>*</span></Form.ControlLabel>
                  <Form.Control name="model_id" placeholder="e.g., my-model" />
                  <Form.HelpText>Unique identifier for this model configuration (cannot contain "/")</Form.HelpText>
                </Form.Group>

                <Form.Group>
                  <Form.ControlLabel>Base Model ID <span style={{ color: 'red' }}>*</span></Form.ControlLabel>
                  <Form.Control name="origin_model_id" placeholder="e.g., meta-llama/Llama-2-7b-hf" />
                  <Form.HelpText>Base model ID and actual model name to load in the container</Form.HelpText>
                </Form.Group>

                <Form.Group>
                  <Form.ControlLabel>Description</Form.ControlLabel>
                  <Form.Control
                    name="description"
                    accepter={Input}
                    as="textarea"
                    rows={3}
                    maxLength={2048}
                    placeholder="Enter model description..."
                  />
                  <Form.HelpText>
                    Optional description (up to 2048 characters) to document this inference model.
                  </Form.HelpText>
                  <Form.HelpText>{(formValue.description || '').length} / 2048 characters</Form.HelpText>
                </Form.Group>

                <Form.Group>
                  <Form.ControlLabel>Instance Type <span style={{ color: 'red' }}>*</span></Form.ControlLabel>
                  <Form.Control 
                    name="instance_type" 
                    accepter={SelectPicker}
                    data={droplets.filter(d => d.available).map(d => ({ label: `${d.slug} (${d.description || ''})`, value: d.slug }))}
                    placeholder="Select instance type"
                    searchable
                  />
                  <Form.HelpText>GPU droplet type (e.g., gpu-6000adax1-48gb)</Form.HelpText>
                </Form.Group>

                <Form.Group>
                  <Form.ControlLabel>Adapter</Form.ControlLabel>
                  <Form.Control 
                    name="adapter" 
                    accepter={SelectPicker}
                    data={savedModels.map(m => ({ 
                      label: m.model_id, 
                      value: m.model_id,
                      description: m.description || '',
                      tags: m.tags || []
                    }))}
                    placeholder="Select adapter from saved models"
                    searchable
                    cleanable
                    renderMenuItem={(label, item) => (
                      <div style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <tbody>
                            <tr>
                              <td style={{ fontWeight: 500, paddingRight: '12px', verticalAlign: 'top', width: '120px' }}>
                                Model ID:
                              </td>
                              <td style={{ fontWeight: 500, verticalAlign: 'top' }}>
                                {label}
                              </td>
                            </tr>
                            {item.description && (
                              <tr>
                                <td style={{ paddingRight: '12px', verticalAlign: 'top', color: '#666', fontSize: '0.875em' }}>
                                  Description:
                                </td>
                                <td style={{ color: '#666', fontSize: '0.875em', verticalAlign: 'top' }}>
                                  {item.description}
                                </td>
                              </tr>
                            )}
                            {item.tags && item.tags.length > 0 && (
                              <tr>
                                <td style={{ paddingRight: '12px', verticalAlign: 'top', color: '#666', fontSize: '0.875em' }}>
                                  Tags:
                                </td>
                                <td style={{ verticalAlign: 'top', fontSize: '0.875em' }}>
                                  <TagGroup>
                                    {item.tags.map((tag, idx) => (
                                      <Tag key={idx}>{tag}</Tag>
                                    ))}
                                  </TagGroup>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  />
                  <Form.HelpText>Select a saved model to use as LoRA adapter (optional)</Form.HelpText>
                </Form.Group>

                <Form.Group>
                  <Form.ControlLabel>Idle Timeout (seconds)</Form.ControlLabel>
                  <Form.Control
                    name="idle_timeout"
                    type="number"
                    min={0}
                    placeholder="1800"
                  />
                  <Form.HelpText>Time in seconds before idle droplet is destroyed (default: 1800 = 30 minutes). Set to 0 to disable automatic cleanup.</Form.HelpText>
                </Form.Group>

                <Form.Group>
                  <Form.ControlLabel>Provider Options (JSON)</Form.ControlLabel>
                  <Form.Control
                    name="provider_options"
                    accepter={Input}
                    as="textarea"
                    rows={3}
                    placeholder='e.g. {"disk_gb": 40}'
                  />
                  <Form.HelpText>Optional provider-specific options as a JSON object. Provider is determined by the selected instance type.</Form.HelpText>
                </Form.Group>
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button 
                onClick={() => {
                  setShowRegisterModal(false);
                  setFormValue({
                    model_id: '',
                    instance_type: '',
                    origin_model_id: '',
                    description: '',
                    adapter: '',
                    idle_timeout: 1800
                  });
                }} 
                appearance="subtle"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleRegister} 
                appearance="primary"
                loading={registering}
                disabled={!formValue.model_id || !formValue.instance_type || !formValue.origin_model_id || formValue.idle_timeout === undefined || formValue.idle_timeout === ''}
              >
                Register
              </Button>
            </Modal.Footer>
          </Modal>

          <InferenceLogDialog 
            show={showLogsModal}
            onClose={() => setShowLogsModal(false)}
            modelId={selectedModel?.id}
          />

          {/* Remove Confirmation Modal */}
          <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)} size="sm">
            <Modal.Header>
              <Modal.Title>Remove Model</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>
                Are you sure you want to remove the model <strong>{selectedModel?.id}</strong>?
              </p>
              <p style={{ marginTop: 10, color: '#999', fontSize: '0.9em' }}>
                This will unregister the model configuration and release any associated droplet.
              </p>
            </Modal.Body>
            <Modal.Footer>
              <Button onClick={() => setShowDeleteModal(false)} appearance="subtle">
                Cancel
              </Button>
              <Button 
                onClick={() => {
                  if (selectedModel) {
                    handleDelete(selectedModel.id);
                  }
                }} 
                color="red" 
                appearance="primary"
              >
                Remove
              </Button>
            </Modal.Footer>
          </Modal>
      </Content>
    </Container>
  );
}

export default Inference;

